import type { Hotspot } from '../../api/types.js';
import type { HotspotScreenPosition, HotspotsSnapshot } from '../../hotspots/types.js';
import { t } from '../../i18n/init.js';
import type { ComponentInstance } from '../component.js';
import { setText } from '../dom-helpers.js';

export interface HotspotDotsProps {
  hotspots: readonly Hotspot[];
  positions: readonly HotspotScreenPosition[];
  snapshot: HotspotsSnapshot;
  // 4.J: when measure mode is active, dots become non-interactive
  // (pointer-events:none, tabindex=-1, aria-hidden=true) so canvas
  // clicks reach the measurement raycast handler instead of opening
  // a hotspot popup. Visual presence is preserved.
  measureActive: boolean;
}

export interface HotspotDotsCallbacks {
  onHotspotOpen: (id: string) => void;
}

const DOT_SIZE_PX = 28;

function styleDot(btn: HTMLButtonElement, active: boolean): void {
  const baseBg = active ? 'rgba(168,85,247,1)' : 'rgba(168,85,247,0.90)';
  const ring = active
    ? '0 0 0 4px rgba(168,85,247,0.40), 0 0 16px rgba(168,85,247,0.50)'
    : '0 0 0 4px rgba(168,85,247,0.20)';
  btn.style.cssText = [
    'position:absolute',
    'top:0',
    'left:0',
    `width:${DOT_SIZE_PX}px`,
    `height:${DOT_SIZE_PX}px`,
    `margin-left:${-DOT_SIZE_PX / 2}px`,
    `margin-top:${-DOT_SIZE_PX / 2}px`,
    'border-radius:9999px',
    `background:${baseBg}`,
    `box-shadow:${ring}`,
    'border:0',
    'color:#ffffff',
    'font-family:ui-monospace,SFMono-Regular,Menlo,monospace',
    'font-size:0.6875rem',
    'font-weight:700',
    'cursor:pointer',
    'pointer-events:auto',
    'transition:box-shadow 120ms,background 120ms',
  ].join(';');
}

export function mountHotspotDots(
  root: HTMLElement,
  initial: HotspotDotsProps,
  callbacks: HotspotDotsCallbacks,
): ComponentInstance<HotspotDotsProps> {
  if (initial.hotspots.length === 0) {
    return {
      update() {
        /* no-op */
      },
      cleanup() {
        /* no-op */
      },
    };
  }

  const wrap = document.createElement('div');
  wrap.classList.add('viewer-ui-interactive');
  wrap.setAttribute('role', 'group');
  wrap.setAttribute('aria-label', t('viewer.hotspot_dots.group_label'));
  // The dots layer is overlaid above the canvas but below modal-style UI.
  wrap.style.cssText = 'position:fixed;inset:0;z-index:11;pointer-events:none';

  const buttons: HTMLButtonElement[] = [];
  initial.hotspots.forEach((h, idx) => {
    const b = document.createElement('button');
    b.type = 'button';
    // 4.M: marker class for the global tap-target rule. On coarse
    // pointer the button bounds expand to 44x44 via padding +
    // background-clip:content-box, so the visual dot stays 28x28
    // while the hit area meets WCAG 2.5.5.
    b.classList.add('viewer-ui-hotspot-dot-button');
    b.dataset['hotspotId'] = h.id;
    b.dataset['hotspotIdx'] = String(idx);
    b.setAttribute('aria-label', t('viewer.hotspot_dots.dot_label', { title: h.title }));
    b.style.transform = 'translate(-9999px, -9999px)'; // start offscreen until first projection
    setText(b, String(idx + 1));
    styleDot(b, false);
    b.addEventListener('click', (ev) => {
      ev.stopPropagation();
      callbacks.onHotspotOpen(h.id);
    });
    wrap.appendChild(b);
    buttons.push(b);
  });

  root.appendChild(wrap);

  let currentSnapshot = initial.snapshot;

  const apply = (props: HotspotDotsProps): void => {
    currentSnapshot = props.snapshot;
    const overallVisible = props.snapshot.visible;
    wrap.style.display = overallVisible && props.hotspots.length > 0 ? 'block' : 'none';
    if (!overallVisible) return;
    // 4.J: while measure mode is active the dots stay visible but become
    // non-interactive — canvas clicks must reach the raycast handler.
    const measureActive = props.measureActive;
    buttons.forEach((btn, idx) => {
      const pos = props.positions[idx];
      if (pos === undefined || !pos.visible) {
        btn.style.display = 'none';
        btn.setAttribute('tabindex', '-1');
        btn.setAttribute('aria-hidden', 'true');
        return;
      }
      // Step 7 fix: styleDot() overwrites cssText, so it MUST run BEFORE
      // we set display + transform — otherwise the freshly-set transform
      // gets nuked and every dot lands at translate(0,0) (visible as a
      // single purple sliver in the top-left of the screen).
      const active = currentSnapshot.openIndex === idx;
      styleDot(btn, active);
      btn.style.display = 'block';
      btn.style.transform = `translate(${pos.x}px, ${pos.y}px)`;
      // Inc C: distance-fade — il dot attivo (popup aperto) resta pieno.
      // DOPO styleDot (che sovrascrive cssText).
      btn.style.opacity = active ? '1' : String(pos.opacity);
      if (active) {
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.removeAttribute('aria-pressed');
      }
      if (measureActive) {
        btn.style.pointerEvents = 'none';
        btn.setAttribute('tabindex', '-1');
        btn.setAttribute('aria-hidden', 'true');
      } else {
        // Step 7 fix: must be explicit 'auto', NOT '' (empty). The wrap
        // container has `pointer-events:none` so clicks pass through to
        // the canvas; resetting pointerEvents to '' removes the inline
        // value and the button inherits 'none' from the parent. styleDot
        // already sets 'auto' in cssText but we have to re-affirm it
        // here because the assignment above (display/transform) ran
        // through the same style object.
        btn.style.pointerEvents = 'auto';
        btn.removeAttribute('aria-hidden');
        btn.removeAttribute('tabindex');
      }
    });
  };

  apply(initial);

  return {
    update(props: HotspotDotsProps) {
      apply(props);
    },
    cleanup() {
      wrap.remove();
    },
  };
}
